/**
 * Repas du widget — simple PONT entre le store widget et les règles PURES
 * partagées avec le SaaS (`buildUnifiedMeals` / `setUnifiedMealQuantity` /
 * `computeMealsTotal`). Le widget ne porte AUCUNE logique repas propre : il lit
 * la disponibilité du store, la passe aux règles communes, et persiste le choix
 * directement sur l'objet `meals[]`/`options[]` de l'availability (store réactif)
 * — pas d'état local volatile. Couvre tous les providers (formats `meals[]` et
 * `options` type RI) via l'unique `buildUnifiedMeals`.
 */
import {
  availabilitiesForGroup,
  buildUnifiedMeals,
  type MealAvailabilityDir,
  type MealGroupBlock,
  type UnifiedMeal,
} from '../../../shared/rules'
import type { Meal } from '../../../shared/types'
import type { WidgetStore } from '../store'

/**
 * Bloc repas `{ group1, group2 }` d'une direction, lu depuis le store widget :
 * `group1` = availability représentative (`primary`) ; `group2` = 1ʳᵉ
 * availability `group_id === 2` (dossier > 4 pax). Même grammaire group1/group2
 * que le SaaS — source UNIQUE pour l'affichage, la persistance et le payload.
 */
export function mealDir(store: WidgetStore, direction: 'outward' | 'return'): MealAvailabilityDir {
  const slot = store.availabilities[direction]
  const all = slot.all ?? []
  return {
    group1: slot.primary ?? undefined,
    group2: availabilitiesForGroup(all, 2)[0] ?? undefined,
  }
}

/** Liste unifiée des repas d'une direction (tous formats providers, group1 + group2). */
export function unifiedMeals(store: WidgetStore, direction: 'outward' | 'return'): UnifiedMeal[] {
  return buildUnifiedMeals(mealDir(store, direction))
}

/** Repas sélectionnés (quantité > 0) d'un bloc groupe → `meals[]` d'un leg de
 *  payload (aligné SaaS `buildAvailability`). */
export function selectedGroupMeals(block: MealGroupBlock | null | undefined): Meal[] {
  return (block?.meals ?? []).filter((m) => (m.quantity ?? 0) > 0)
}

/** Total repas d'une direction : Σ (prix × quantité), toutes sources confondues. */
function directionMealsTotal(store: WidgetStore, direction: 'outward' | 'return'): number {
  return unifiedMeals(store, direction).reduce((sum, m) => sum + m.amount * m.quantity, 0)
}

/** Total repas du dossier (aller + retour) — additionné aux tarifs pour le prix. */
export function mealsTotal(store: WidgetStore): number {
  return directionMealsTotal(store, 'outward') + directionMealsTotal(store, 'return')
}

/**
 * `true` si au moins un repas est proposé (aller ou retour) → l'étape Options
 * existe. Gating basé sur la liste unifiée (tous formats providers) au lieu de
 * `options?.length` seul, qui ratait les providers à repas `meals[]`.
 */
export function hasMealOptions(store: WidgetStore): boolean {
  return unifiedMeals(store, 'outward').length > 0 || unifiedMeals(store, 'return').length > 0
}
